/** @format */

/**
 * Trace 详情 — Span 调用拓扑（antv/x6）。
 * 筛选规则与 TraceSpanTree 一致：Kind / Name / 仅异常；点击节点触发 onSelectSpan。
 *
 * 拓扑数据来源（见 topology/index）：
 * - langfuse：WorkflowGraph 静态图 + Executions
 * - langGraph：Span Attributes 推断 LangGraph 步骤
 * - spanTree：普通 OTEL ParentSpanId 父子树
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Graph } from '@antv/x6'
import { useFullscreen, useToggle } from 'react-use'
import { _$, Icon, Tooltip } from '@king-design/react'
import { secondsFormat } from 'src/utils'
import type { TraceAnalysisDetailResponse, TraceAnalysisDetailSpan } from '../typescript'
import { getDurationBarTone } from './TraceSpanTree'
import { LANGGRAPH_ROOT_KEY } from './langgraphSpanTree'
import { buildTraceTopologyModel, hasTraceTopologyData } from './topology'
import type {
  LangfuseWorkflowEdge,
  LangfuseWorkflowNode,
  TopologyLayoutDirection,
  TraceTopologyModel
} from './topology'
import { findWorkflowExecutionItem, getNextWorkflowExecutionIndex } from './topology'
import {
  getMatchedSpanIds,
  isSpanFilterActive,
  spanNodeLabel,
  spanStatusVisual
} from './traceSpanShared'

export interface TraceSpanTopologyProps {
  className?: string
  detail: TraceAnalysisDetailResponse | null | undefined
  selectedTags?: string[]
  searchValue?: string
  showErrorSpans?: boolean
  /** 当前选中的 SpanId（由父组件管理） */
  selectedSpanId?: string | null
  /** 点击节点时触发 */
  onSelectSpan?: (span: TraceAnalysisDetailSpan) => void
}

interface TopologyToggleOverlay {
  id: string
  x: number
  y: number
  collapsed: boolean
}

const NODE_WIDTH = 168
const NODE_HEIGHT = 60
const GAP_X = 28
const GAP_Y = 40
const TOPOLOGY_ZOOM_MIN = 0.25
const TOPOLOGY_ZOOM_MAX = 2.5
const TOPOLOGY_ZOOM_STEP = 0.12

const HIDDEN_PORT_ATTRS = {
  circle: {
    r: 1,
    magnet: true,
    visibility: 'hidden' as const
  }
}

const SPAN_NODE_PORTS = {
  groups: {
    top: { position: 'top', attrs: HIDDEN_PORT_ATTRS },
    bottom: { position: 'bottom', attrs: HIDDEN_PORT_ATTRS },
    left: { position: 'left', attrs: HIDDEN_PORT_ATTRS },
    right: { position: 'right', attrs: HIDDEN_PORT_ATTRS }
  },
  items: [
    { id: 'top', group: 'top' },
    { id: 'bottom', group: 'bottom' },
    { id: 'left', group: 'left' },
    { id: 'right', group: 'right' }
  ]
}

interface ParentChildEdgePorts {
  sourcePort: 'top' | 'bottom' | 'left' | 'right'
  targetPort: 'top' | 'bottom' | 'left' | 'right'
}

function getParentChildEdgePorts(direction: TopologyLayoutDirection): ParentChildEdgePorts {
  if (direction === 'horizontal') {
    return {
      sourcePort: 'right',
      targetPort: 'left'
    }
  }
  return {
    sourcePort: 'bottom',
    targetPort: 'top'
  }
}

function graphPointToContainer(graph: Graph, point: { x: number; y: number }) {
  const scale = graph.zoom()
  const { tx, ty } = graph.translate()
  return {
    x: point.x * scale + tx,
    y: point.y * scale + ty
  }
}

function getNodeExitPoint(
  graph: Graph,
  nodeId: string,
  direction: TopologyLayoutDirection
): { x: number; y: number } | null {
  const node = graph.getCellById(nodeId)
  if (!node?.isNode()) {
    return null
  }
  const bbox = node.getBBox()
  if (direction === 'horizontal') {
    return { x: bbox.x + bbox.width, y: bbox.y + bbox.height / 2 }
  }
  return { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height }
}

function spanHasChildren(
  childrenMap: Map<string, TraceAnalysisDetailSpan[]>,
  spanId: string
): boolean {
  return (childrenMap.get(spanId)?.length ?? 0) > 0
}

/** 更新 Langfuse 工作流节点文案与样式，避免整图重绘（点击轮询执行 Span 时用） */
function patchLangfuseWorkflowNodeDisplay(
  x6Node: {
    getData: () => unknown
    setData: (data: unknown) => void
    attr: (path: string, value: unknown) => void
  },
  workflowNode: LangfuseWorkflowNode,
  span: TraceAnalysisDetailSpan,
  executionLine: string,
  matchedSpanIds: Set<string>,
  filterActive: boolean
) {
  const data = x6Node.getData() as {
    span?: TraceAnalysisDetailSpan
    workflowNode?: LangfuseWorkflowNode
    status?: 'ok' | 'warn' | 'err'
    hasChildren?: boolean
  }
  const isFilterMatched = matchedSpanIds.has(span.SpanId)
  const dimmed = filterActive && !isFilterMatched
  const thirdLine =
    workflowNode.totalDurationMs != null
      ? `${_$('累计耗时')}：${secondsFormat(workflowNode.totalDurationMs)}`
      : `${_$('服务名')}：${span.ServiceName}`
  x6Node.setData({ ...data, span })
  x6Node.attr('duration/text', thirdLine)
  x6Node.attr('serviceName/text', executionLine)
  x6Node.attr('serviceName/fill', span.BgColor)
  x6Node.attr('body/fill', isFilterMatched ? 'rgba(255, 251, 0, 0.55)' : '#fff')
  x6Node.attr('body/opacity', dimmed ? 0.38 : 1)
  x6Node.attr('body/stroke', span.BgColor)
  x6Node.attr('body/strokeWidth', 2)
}

function fitTopologyGraph(graph: Graph) {
  graph.zoomToFit({
    padding: 48,
    minScale: TOPOLOGY_ZOOM_MIN,
    maxScale: TOPOLOGY_ZOOM_MAX
  })
  graph.centerContent({ padding: 48 })
}

/** X6 resize 会写入 inline 宽高，退出全屏后需清除再按 CSS 重算 */
function clearGraphContainerInlineSize(container: HTMLElement) {
  container.style.removeProperty('width')
  container.style.removeProperty('height')
  container.querySelectorAll<HTMLElement>('.x6-graph, .x6-graph-svg').forEach(el => {
    el.style.removeProperty('width')
    el.style.removeProperty('height')
  })
}

function readContainerGraphSize(container: HTMLElement) {
  const { width, height } = container.getBoundingClientRect()
  return {
    width: Math.round(width),
    height: Math.round(height)
  }
}

/** 将 X6 画布尺寸与容器 DOM 对齐（不改动缩放） */
function syncGraphContainerSize(graph: Graph, container: HTMLElement) {
  clearGraphContainerInlineSize(container)
  // 强制按样式表重新布局后再读取尺寸
  void container.offsetHeight
  const { width, height } = readContainerGraphSize(container)
  if (width > 0 && height > 0) {
    graph.resize(width, height)
  } else {
    graph.resize()
  }
}

/** 同步容器尺寸后再适应画布（全屏切换等场景需先 resize） */
function relayoutTopologyGraph(graph: Graph, container: HTMLElement) {
  syncGraphContainerSize(graph, container)
  requestAnimationFrame(() => {
    syncGraphContainerSize(graph, container)
    fitTopologyGraph(graph)
  })
}

/** 1:1 实际比例展示并居中 */
function applyTopologyGraphOneToOne(graph: Graph) {
  graph.zoomTo(clampTopologyZoom(1))
  graph.centerContent({ padding: 48 })
}

function clampTopologyZoom(scale: number): number {
  return Math.min(TOPOLOGY_ZOOM_MAX, Math.max(TOPOLOGY_ZOOM_MIN, scale))
}

const TraceSpanTopology: React.FC<TraceSpanTopologyProps> = ({
  className,
  detail,
  selectedTags = [],
  searchValue = '',
  showErrorSpans = false,
  selectedSpanId = null,
  onSelectSpan
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const graphRef = useRef<Graph | null>(null)
  const onSelectSpanRef = useRef(onSelectSpan)
  const layoutDirectionRef = useRef<TopologyLayoutDirection>('vertical')
  const collapsedSpanIdsRef = useRef<Set<string>>(new Set())
  /** 记录各 Langfuse 节点当前展示到第几次执行（ExecutionIndex，1-based） */
  const workflowExecutionIndexRef = useRef<Map<string, number>>(new Map())
  /** 图初始化只绑定一次 node:click，具体逻辑通过 ref 注入以读取最新筛选状态 */
  const handleLangfuseNodeClickRef = useRef<
    (node: {
      getData: () => unknown
      setData: (data: unknown) => void
      attr: (path: string, value: unknown) => void
    }) => void
  >(() => undefined)
  const syncToggleOverlayRef = useRef<() => void>()
  const [collapsedSpanIds, setCollapsedSpanIds] = useState<Set<string>>(() => new Set())
  const [toggleOverlays, setToggleOverlays] = useState<TopologyToggleOverlay[]>([])
  const [fullscreenOn, toggleFullscreen] = useToggle(false)
  const isFullscreen = useFullscreen(wrapRef, fullscreenOn, {
    onClose: () => toggleFullscreen(false)
  })

  onSelectSpanRef.current = onSelectSpan

  const toggleSpanCollapse = useCallback((spanId: string) => {
    setCollapsedSpanIds(prev => {
      const next = new Set(prev)
      if (next.has(spanId)) {
        next.delete(spanId)
      } else {
        next.add(spanId)
      }
      return next
    })
  }, [])

  const allSpans = detail?.Spans ?? []

  const syncToggleOverlay = useCallback(() => {
    const graph = graphRef.current
    if (!graph || !graph.getNodes().length) {
      setToggleOverlays([])
      return
    }

    const direction = layoutDirectionRef.current
    const collapsed = collapsedSpanIdsRef.current
    const overlays: TopologyToggleOverlay[] = []

    graph.getNodes().forEach(node => {
      if (!node.getData()?.hasChildren) {
        return
      }
      const exitPoint = getNodeExitPoint(graph, node.id, direction)
      if (!exitPoint) {
        return
      }
      const { x, y } = graphPointToContainer(graph, exitPoint)
      overlays.push({
        id: node.id,
        x,
        y,
        collapsed: collapsed.has(node.id)
      })
    })

    setToggleOverlays(overlays)
  }, [])

  syncToggleOverlayRef.current = syncToggleOverlay

  useEffect(() => {
    setCollapsedSpanIds(new Set())
    workflowExecutionIndexRef.current = new Map()
  }, [detail?.TraceId])

  const matchedSpanIds = useMemo(
    () => getMatchedSpanIds(allSpans, { searchValue, selectedTags, showErrorSpans }),
    [allSpans, searchValue, selectedTags, showErrorSpans]
  )

  const filterActive = isSpanFilterActive(searchValue, selectedTags, showErrorSpans)

  const formatWorkflowExecutionLine = useCallback(
    (current: number, total: number) => `${_$('执行')}：${current}/${total}${_$('次')}`,
    []
  )

  // Langfuse 多执行节点：每次点击按 ExecutionIndex 顺序轮询 Span，并刷新「执行：n/N次」文案
  handleLangfuseNodeClickRef.current = x6Node => {
    const data = x6Node.getData() as {
      span?: TraceAnalysisDetailSpan
      workflowNode?: LangfuseWorkflowNode
    }
    const workflowNode = data?.workflowNode
    const executionItems = workflowNode?.executionItems ?? []

    if (workflowNode && executionItems.length > 1) {
      const prev = workflowExecutionIndexRef.current.get(workflowNode.nodeId)
      const nextExecutionIndex = getNextWorkflowExecutionIndex(executionItems, prev)
      workflowExecutionIndexRef.current.set(workflowNode.nodeId, nextExecutionIndex)
      const item = findWorkflowExecutionItem(executionItems, nextExecutionIndex)
      const span = item?.span
      if (!span) {
        return
      }
      const total = workflowNode.executionCount ?? executionItems.length
      patchLangfuseWorkflowNodeDisplay(
        x6Node,
        workflowNode,
        span,
        formatWorkflowExecutionLine(nextExecutionIndex, total),
        matchedSpanIds,
        filterActive
      )
      onSelectSpanRef.current?.(span)
      return
    }
    const span = data?.span ?? workflowNode?.span
    if (span) {
      onSelectSpanRef.current?.(span)
    }
  }

  const topologyModel = useMemo((): TraceTopologyModel => {
    return buildTraceTopologyModel(detail, allSpans, collapsedSpanIds, {
      nodeWidth: NODE_WIDTH,
      nodeHeight: NODE_HEIGHT,
      gapX: GAP_X,
      gapY: GAP_Y
    })
  }, [allSpans, collapsedSpanIds, detail])

  layoutDirectionRef.current = topologyModel.layoutDirection
  collapsedSpanIdsRef.current = collapsedSpanIds

  const handleZoomIn = useCallback(() => {
    const graph = graphRef.current
    if (!graph) {
      return
    }
    graph.zoomTo(clampTopologyZoom(graph.zoom() + TOPOLOGY_ZOOM_STEP))
  }, [])

  const handleZoomOut = useCallback(() => {
    const graph = graphRef.current
    if (!graph) {
      return
    }
    graph.zoomTo(clampTopologyZoom(graph.zoom() - TOPOLOGY_ZOOM_STEP))
  }, [])

  const handleZoomOneToOne = useCallback(() => {
    const graph = graphRef.current
    const container = containerRef.current
    if (!graph || !container) {
      return
    }
    syncGraphContainerSize(graph, container)
    applyTopologyGraphOneToOne(graph)
  }, [])

  const handleZoomFit = useCallback(() => {
    const graph = graphRef.current
    const container = containerRef.current
    if (!graph || !container) {
      return
    }
    relayoutTopologyGraph(graph, container)
  }, [])

  /** 初始化 X6 画布（只执行一次） */
  useEffect(() => {
    if (!containerRef.current) {
      return undefined
    }

    const graph = new Graph({
      container: containerRef.current,
      autoResize: false,
      panning: {
        enabled: true
      },
      mousewheel: {
        enabled: true,
        zoomAtMousePosition: true,
        minScale: TOPOLOGY_ZOOM_MIN,
        maxScale: TOPOLOGY_ZOOM_MAX
      },
      interacting: {
        nodeMovable: false,
        edgeMovable: false
      },
      background: {
        color: '#fafafa'
      }
    })

    graph.on('node:click', ({ node }) => {
      handleLangfuseNodeClickRef.current(node)
    })

    const scheduleSyncToggleOverlay = () => {
      requestAnimationFrame(() => syncToggleOverlayRef.current?.())
    }

    graph.on('scale', scheduleSyncToggleOverlay)
    graph.on('translate', scheduleSyncToggleOverlay)

    graphRef.current = graph

    const container = containerRef.current
    const resizeObserver =
      container &&
      new ResizeObserver(() => {
        if (!graphRef.current || !containerRef.current) {
          return
        }
        syncGraphContainerSize(graphRef.current, containerRef.current)
        scheduleSyncToggleOverlay()
      })
    resizeObserver?.observe(container)

    const scheduleRelayoutAfterFullscreen = () => {
      if (!graphRef.current || !containerRef.current) {
        return
      }
      const graph = graphRef.current
      const canvas = containerRef.current
      relayoutTopologyGraph(graph, canvas)
      window.setTimeout(() => {
        relayoutTopologyGraph(graph, canvas)
        scheduleSyncToggleOverlay()
      }, 150)
    }

    const onFullscreenChange = () => {
      window.setTimeout(scheduleRelayoutAfterFullscreen, 0)
    }
    document.addEventListener('fullscreenchange', onFullscreenChange)

    return () => {
      resizeObserver?.disconnect()
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      graph.dispose()
      graphRef.current = null
    }
  }, [])

  /** 全屏状态变化后重算画布（配合 fullscreenchange，覆盖 Esc 退出等场景） */
  useEffect(() => {
    const graph = graphRef.current
    const container = containerRef.current
    if (!graph || !container) {
      return
    }
    const timer = window.setTimeout(() => {
      relayoutTopologyGraph(graph, container)
    }, 0)
    return () => window.clearTimeout(timer)
  }, [isFullscreen])

  /** 根据 Spans / 筛选刷新节点与边（不依赖 selectedSpanId，避免点击选中时重绘画布） */
  useEffect(() => {
    const graph = graphRef.current
    if (!graph) {
      return
    }

    graph.clearCells()
    if (!topologyModel.nodes.length) {
      setToggleOverlays([])
      return
    }

    const addSpanNode = (span: TraceAnalysisDetailSpan, x: number, y: number) => {
      const status = spanStatusVisual(span)
      const isFilterMatched = matchedSpanIds.has(span.SpanId)
      const dimmed = filterActive && !isFilterMatched
      const durationLabel = secondsFormat(span.DurationMs)
      const durationTone = getDurationBarTone(span.DurationMs)
      const hasChildren = spanHasChildren(
        topologyModel.mode === 'spanTree' || topologyModel.mode === 'langGraph'
          ? topologyModel.childrenMap
          : new Map(),
        span.SpanId
      )

      graph.addNode({
        id: span.SpanId,
        shape: 'rect',
        x,
        y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        data: { span, status, hasChildren },
        ports: { ...SPAN_NODE_PORTS },
        markup: [
          { tagName: 'rect', selector: 'body' },
          { tagName: 'text', selector: 'name' },
          { tagName: 'text', selector: 'serviceName' },
          { tagName: 'text', selector: 'duration' }
        ],
        attrs: {
          body: {
            fill: isFilterMatched ? 'rgba(255, 251, 0, 0.55)' : '#fff',
            stroke: span?.BgColor,
            strokeWidth: 2,
            rx: 6,
            ry: 6,
            opacity: dimmed ? 0.38 : 1
          },
          name: {
            text: spanNodeLabel(span),
            ref: 'body',
            refX: 10,
            refY: 8,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            fontSize: 12,
            fontWeight: 600,
            fill: 'rgba(0, 0, 0, 0.85)',
            textWrap: {
              width: -20,
              height: 22,
              ellipsis: true,
              breakWord: true
            }
          },
          duration: {
            text: `${_$('耗时')}：${durationLabel}`,
            ref: 'body',
            refX: 10,
            refY: 26,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            fontSize: 11,
            fill: durationTone
          },
          serviceName: {
            text: `${_$('服务名')}：${span?.ServiceName}`,
            ref: 'body',
            refX: 10,
            refY: 42,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            fontSize: 11,
            fill: span?.BgColor,
            textWrap: {
              width: -20,
              height: 22,
              ellipsis: true,
              breakWord: true
            }
          }
        }
      })
    }

    const addWorkflowNode = (node: LangfuseWorkflowNode) => {
      const executionItems = node.executionItems ?? []
      const total = node.executionCount ?? executionItems.length
      // 重绘时优先沿用用户已轮询到的 ExecutionIndex，否则用接口初始次序
      const storedExecutionIndex = workflowExecutionIndexRef.current.get(node.nodeId)
      const displayIndex =
        storedExecutionIndex ?? node.currentExecutionIndex ?? executionItems[0]?.executionIndex ?? 0
      const span =
        storedExecutionIndex !== undefined
          ? findWorkflowExecutionItem(executionItems, storedExecutionIndex)?.span
          : node.span
      const status = span ? spanStatusVisual(span) : 'ok'
      const isFilterMatched = span ? matchedSpanIds.has(span.SpanId) : false
      const dimmed = filterActive && span && !isFilterMatched
      const durationTone =
        node.totalDurationMs != null ? getDurationBarTone(node.totalDurationMs) : 'rgba(0,0,0,0.45)'
      const secondLine =
        node.kind === 'workflow' && total > 0
          ? formatWorkflowExecutionLine(displayIndex, total) // duration 行：当前第几次 / 总次数
          : node.kind === 'virtual'
          ? _$('虚拟节点')
          : ''
      const thirdLine =
        node.totalDurationMs != null
          ? `${_$('累计耗时')}：${secondsFormat(node.totalDurationMs)}` // serviceName 行：节点累计耗时
          : span?.ServiceName
          ? `${_$('服务名')}：${span.ServiceName}`
          : ''

      graph.addNode({
        id: node.nodeId,
        shape: 'rect',
        x: node.x,
        y: node.y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        data: { span, workflowNode: node, status, hasChildren: false },
        ports: { ...SPAN_NODE_PORTS },
        markup: [
          { tagName: 'rect', selector: 'body' },
          { tagName: 'text', selector: 'name' },
          { tagName: 'text', selector: 'serviceName' },
          { tagName: 'text', selector: 'duration' }
        ],
        attrs: {
          body: {
            fill: isFilterMatched ? 'rgba(255, 251, 0, 0.55)' : '#fff',
            stroke: span?.BgColor || '#d9d9d9',
            strokeWidth: 2,
            rx: 6,
            ry: 6,
            opacity: dimmed ? 0.38 : 1
          },
          name: {
            text: node.label,
            ref: 'body',
            refX: 10,
            refY: 8,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            fontSize: 12,
            fontWeight: 600,
            fill: 'rgba(0, 0, 0, 0.85)',
            textWrap: {
              width: -20,
              height: 22,
              ellipsis: true,
              breakWord: true
            }
          },
          duration: {
            text: thirdLine,
            ref: 'body',
            refX: 10,
            refY: 26,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            fontSize: 11,
            fill: durationTone
          },
          serviceName: {
            text: secondLine,
            ref: 'body',
            refX: 10,
            refY: 42,
            textAnchor: 'start',
            textVerticalAnchor: 'top',
            fontSize: 11,
            fill: span?.BgColor || 'rgba(0, 0, 0, 0.45)',
            textWrap: {
              width: -20,
              height: 22,
              ellipsis: true,
              breakWord: true
            }
          }
        }
      })
    }

    if (topologyModel.mode === 'langfuse') {
      topologyModel.nodes.forEach(node => addWorkflowNode(node))
    } else {
      topologyModel.nodes.forEach(({ span, x, y }) => addSpanNode(span, x, y))
    }

    const nodeIds = new Set(graph.getNodes().map(node => node.id))
    const edgePorts = getParentChildEdgePorts(topologyModel.layoutDirection)
    const edgeMarker = { name: 'block' as const, width: 8, height: 6 }
    const addGraphEdge = (
      from: string,
      to: string,
      kind: 'normal' | 'conditional' | string = 'normal',
      bidirectional = false // Langfuse 回路边合并后，source/target 双端箭头
    ) => {
      if (!nodeIds.has(from) || !nodeIds.has(to)) {
        return
      }
      const isConditional = kind === 'conditional'
      graph.addEdge({
        id: bidirectional ? `${from}<->${to}` : `${from}->${to}`,
        source: { cell: from, port: edgePorts.sourcePort },
        target: { cell: to, port: edgePorts.targetPort },
        attrs: {
          line: {
            stroke: isConditional ? '#91caff' : '#bfbfbf',
            strokeWidth: isConditional ? 1.5 : 1,
            strokeDasharray: isConditional ? '6 4' : undefined,
            sourceMarker: bidirectional ? edgeMarker : undefined,
            targetMarker: edgeMarker
          }
        }
      })
    }

    if (topologyModel.mode === 'langfuse') {
      topologyModel.edges.forEach((edge: LangfuseWorkflowEdge) =>
        addGraphEdge(edge.from, edge.to, edge.kind, edge.bidirectional)
      )
    } else if (topologyModel.mode === 'langGraph') {
      topologyModel.edges.forEach(edge => addGraphEdge(edge.from, edge.to))
    } else {
      const { visibleSpanIds, childrenMap } = topologyModel
      childrenMap.forEach((children, parentKey) => {
        if (parentKey === LANGGRAPH_ROOT_KEY) return
        if (collapsedSpanIds.has(parentKey)) return
        children.forEach(child => {
          if (visibleSpanIds.has(child.SpanId)) {
            addGraphEdge(parentKey, child.SpanId)
          }
        })
      })
    }

    if (containerRef.current) {
      syncGraphContainerSize(graph, containerRef.current)
    }
    applyTopologyGraphOneToOne(graph)
    requestAnimationFrame(() => syncToggleOverlayRef.current?.())
  }, [topologyModel, matchedSpanIds, filterActive, collapsedSpanIds, formatWorkflowExecutionLine])

  /** Langfuse 节点：外部选中 Span 时同步执行次序文案 */
  useEffect(() => {
    if (topologyModel.mode !== 'langfuse') {
      return
    }
    const graph = graphRef.current
    if (!graph || !selectedSpanId) {
      return
    }

    graph.getNodes().forEach(x6Node => {
      const data = x6Node.getData() as {
        span?: TraceAnalysisDetailSpan
        workflowNode?: LangfuseWorkflowNode
      }
      const workflowNode = data?.workflowNode
      const executionItems = workflowNode?.executionItems ?? []
      if (!workflowNode || executionItems.length <= 1) {
        return
      }

      const item = executionItems.find(entry => entry.span.SpanId === selectedSpanId)
      if (!item) {
        return
      }

      workflowExecutionIndexRef.current.set(workflowNode.nodeId, item.executionIndex)
      const total = workflowNode.executionCount ?? executionItems.length
      patchLangfuseWorkflowNodeDisplay(
        x6Node,
        workflowNode,
        item.span,
        formatWorkflowExecutionLine(item.executionIndex, total),
        matchedSpanIds,
        filterActive
      )
    })
  }, [selectedSpanId, topologyModel, matchedSpanIds, filterActive, formatWorkflowExecutionLine])

  /** 仅更新节点选中描边，不移动视口 */
  useEffect(() => {
    const graph = graphRef.current
    if (!graph) {
      return
    }

    graph.getNodes().forEach(node => {
      const data = node.getData() as {
        span?: TraceAnalysisDetailSpan
        workflowNode?: LangfuseWorkflowNode
        status?: 'ok' | 'warn' | 'err'
      }
      const span = data?.span ?? data?.workflowNode?.span
      // const workflowNode = data?.workflowNode
      if (!span) {
        return
      }
      const isSelected = selectedSpanId === span.SpanId
      node.attr('body/stroke', isSelected ? 'rgb(0, 145, 234)' : span?.BgColor)
      node.attr('body/strokeWidth', isSelected ? 2.5 : 2)
    })
  }, [selectedSpanId, topologyModel])

  if (!hasTraceTopologyData(detail, allSpans)) {
    return (
      <div className={className}>
        <div className="trace-span-topology-empty">{_$('暂无 Span')}</div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div
        ref={wrapRef}
        className={`trace-span-topology-wrap${isFullscreen ? ' is-fullscreen' : ''}`}
      >
        <div className="trace-span-topology-toolbar">
          <Tooltip content={_$('放大')} theme="light">
            <Icon className="ion-plus" size={14} onClick={handleZoomIn} />
          </Tooltip>
          <Tooltip content={_$('缩小')} theme="light">
            <Icon className="ion-minus" size={14} onClick={handleZoomOut} />
          </Tooltip>
          <Tooltip content={_$('1:1')} theme="light">
            <button
              type="button"
              className="trace-span-topology-zoom-one"
              onClick={handleZoomOneToOne}
            >
              1:1
            </button>
          </Tooltip>
          <Tooltip content={_$('适应画布')} theme="light">
            <Icon className="ion-android-apps" size={14} onClick={handleZoomFit} />
          </Tooltip>
          <Tooltip content={isFullscreen ? _$('取消全屏') : _$('全屏')} theme="light">
            <Icon
              className={isFullscreen ? 'ion-android-contract' : 'ion-android-expand'}
              size={14}
              onClick={toggleFullscreen}
            />
          </Tooltip>
        </div>
        <div className="trace-span-topology-canvas-shell">
          <div ref={containerRef} className="trace-span-topology-canvas" />
          <div className="trace-span-topology-toggles">
            {toggleOverlays.map(item => (
              <Tooltip
                key={item.id}
                content={item.collapsed ? _$('展开') : _$('收起')}
                theme="light"
              >
                <button
                  type="button"
                  className="trace-span-topology-toggle"
                  style={{ left: item.x, top: item.y }}
                  onClick={event => {
                    event.stopPropagation()
                    toggleSpanCollapse(item.id)
                  }}
                >
                  <Icon
                    className={item.collapsed ? 'k-icon-add-circled' : 'k-icon-more-circled'}
                    size={14}
                  />
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default styled(TraceSpanTopology)`
  height: 100%;

  .trace-span-topology-wrap {
    position: relative;
    width: 100%;

    &.is-fullscreen,
    &:fullscreen {
      display: flex;
      flex-direction: column;
      width: 100vw;
      height: 100vh;
      background: #fafafa;
    }

    &.is-fullscreen .trace-span-topology-canvas-shell,
    &:fullscreen .trace-span-topology-canvas-shell {
      flex: 1;
      width: 100%;
      height: auto !important;
      min-height: 0;
    }
  }

  .trace-span-topology-canvas-shell {
    position: relative;
    width: 100%;
    height: calc(100vh - 400px);
    border-radius: 4px;
    overflow: hidden;
  }

  .trace-span-topology-toggles {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
  }

  .trace-span-topology-toggle {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 1px #d9d9d9;
    color: rgba(0, 0, 0, 0.65);
    transform: translate(-50%, -50%);
    pointer-events: auto;
    cursor: pointer;
    line-height: 1;

    &:hover {
      color: rgb(0, 145, 234);
      box-shadow: 0 0 0 1px rgba(0, 145, 234, 0.35);
    }
  }

  .trace-span-topology-toolbar {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid #f0f0f0;
    .k-icon {
      cursor: pointer;
      &:hover {
        color: rgb(0, 145, 234);
        background: rgba(0, 145, 234, 0.06);
      }
    }
    .trace-span-topology-zoom-one {
      min-width: 28px;
      height: 22px;
      padding: 0 4px;
      border: none;
      border-radius: 2px;
      background: transparent;
      color: rgba(0, 0, 0, 0.65);
      font-size: 11px;
      font-weight: 500;
      line-height: 22px;
      cursor: pointer;

      &:hover {
        color: rgb(0, 145, 234);
        background: rgba(0, 145, 234, 0.06);
      }
    }
  }

  .trace-span-topology-canvas {
    width: 100%;
    height: 100%;
  }

  .trace-span-topology-empty {
    padding: 48px;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
    border-radius: 4px;
    background: #fafafa;
  }
`
